/*
HTTP Status Code
Informational responses (100–199)
Successful responses (200–299)
Redirects (300–399)
Client errors (400–499)
Server errors (500–599)
*/
import * as Constants from '@app/common/constants';
import env from '@app/env.json';
import {getAccessToken} from '@app/helper/login';
import {authTokenActions} from '@app/screens/generic/Login/redux';
import {store} from '../../App';
import {client} from './client';
import {appSliceActions} from '@app/store/reducers/appSlice';
import {ToasterService} from '@app/services';
import {ToastType} from '@app/services/toaster';
import {trackInsightTrace, generateUUID} from '@app/utils/applicationInsight';

let numberOfAjaxCAllPending = 0;
// Reports/downloads route to the Reporting Service (prod-DB replica) so they no longer load the
// production DB directly. Falls back to the standard API_HOST for environments not yet provisioned.
// REPORTING_SERVICE_API_HOST is only defined in some environment files so far (e.g. envs/dev3NG.json);
// the inline cast keeps this compiling regardless of which env.json is active at build time.
const reportingServiceHost =
  (env as typeof env & {REPORTING_SERVICE_API_HOST?: string}).REPORTING_SERVICE_API_HOST ||
  env.API_HOST;

// Configuration for payload-dependent routing (highest priority)
// These APIs route based on payload content (e.g., download flags)
const PAYLOAD_BASED_ROUTING: Record<string, {
  matcher: (data: any) => boolean;
  host: string;
}> = {
  // InTransit Challan filtered data - routes to alternate host when downloading
  '/inventoryadmin/intransitchallansearchfilterdata': {
    matcher: (data) => data?.isDownload === true,
    host: reportingServiceHost,
  },
  // Inventory ledger report - routes to alternate host for downloads
  '/inventoryadmin/inventoryledgerreport': {
    matcher: (data) => data?.apiFor === 'Download',
    host: reportingServiceHost,
  },
  '/inventoryadmin/adminpendencyreportdata': {
    matcher: (data) => data?.apiFor === "DownLoad",
    host: reportingServiceHost,
  },
  // Blob download proxy (shared app-wide) - routes to reporting host only when the caller flags it.
  // No leading slash: fileAccessService builds the url from apiPaths.DOCUMENT_DOWNLOAD ('document/download')
  // and payload routes are matched with startsWith on the raw url.
  'document/download': {
    matcher: (data) => data?.useReportingHost === true,
    host: reportingServiceHost,
  },
};

// Configuration for APIs that need alternate base URLs
const API_BASE_URL_CONFIG: Record<string, string> = {
  '/expensereport/tasummary': reportingServiceHost,
  '/expensereport/bifurcationreport': reportingServiceHost,
  '/expensereport/yearlyreport': reportingServiceHost,
  '/expensereport/statusreport': reportingServiceHost,
  '/expensereport/distancenotmadereport': reportingServiceHost,
  '/planandmeet/download': reportingServiceHost,
  '/expenseadmin/expensetypesettings/downloadcsv?returnInBase64=true': reportingServiceHost,
  '/admininventorycompliance/inventorycomplianceexceldownload/': reportingServiceHost,
  '/expenseadmin/statusreport/downloadcsv?returnInBase64=true': reportingServiceHost, // module-specific-utilities/generate-report
  '/inventoryadmin/challanreceiveaudittrail/downloadcsv': reportingServiceHost, // inventory-management/InTransit-challan -- this url receives search params as well, so we check for partial match in getBaseURL function
  '/inventoryadmin/cnfaudittrail/downloadcsv': reportingServiceHost, // inventory-management/cnf-return request
  '/inventoryadmin/challanshortquantityaudittrail/downloadcsv': reportingServiceHost, // inventory-management/challan-short-quantity-audit-trail
  'leave/downloadleavedata': reportingServiceHost, // leave-management/download-leave-data
};

/**
 * Get the appropriate base URL for the given API path
 * Priority: Payload-based routing > Exact match > Partial match > Default
 * @param url API endpoint path
 * @param data Request payload (optional, used for payload-based routing)
 * @returns Base URL to use for the request
 */
const getBaseURL = (url: string, data?: any): string => {
  // Priority 1: Check payload-based routing (highest priority)
  // Handles cases where same URL needs different hosts based on payload content
  const payloadRouteKey = Object.keys(PAYLOAD_BASED_ROUTING).find(key => 
    url.startsWith(key)  // Matches base path, ignores dynamic segments & query params
  );
  if (payloadRouteKey) {
    const payloadRoute = PAYLOAD_BASED_ROUTING[payloadRouteKey];
    const matcherResult = payloadRoute.matcher(data);
    if (matcherResult) {
      return payloadRoute.host;
    }
  }
  
  // Priority 2: Check exact match
  if (API_BASE_URL_CONFIG[url]) {
    return API_BASE_URL_CONFIG[url];
  }
  
  // Priority 3: Check if URL starts with any configured path
  const matchingPath = Object.keys(API_BASE_URL_CONFIG).find(path => 
    url.startsWith(path) || url.includes(path)
  );
  
  return matchingPath ? API_BASE_URL_CONFIG[matchingPath] : env.API_HOST;
};

client.interceptors.request.use(
  config => {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending + 1;
    store.dispatch(appSliceActions.setAppLoading(true));
    return config;
  },
  error => {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
    return Promise.reject(error);
  },
);

client.interceptors.response.use(response => {
  if (response.status === Constants.NOT_AUTHORIZED) {
    ToasterService.showToaster('Session Expired', ToastType.ERROR);
    store.dispatch(authTokenActions.signOut());
  }
  if (numberOfAjaxCAllPending > 0) {
    numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
  }
  if (numberOfAjaxCAllPending <= 0) {
    store.dispatch(appSliceActions.setAppLoading(false));
  }

  return response;
});

const getNetworkResponse = async (config: any, apiPath: any) => {
  return client(config)
    .then(async function (response: any) {
      const trackObj = {
        url: config.url,
        response: response,
        type: config.method,
        CorrelationId: config.headers.CorrelationId,
      };
      if (config.method === 'POST') {
        trackInsightTrace(`Post call for api ${config.url}`, {
          ...trackObj,
          payload: config.data,
        });
      } else {
        trackInsightTrace(`Get call for api ${config.url}`, trackObj);
      }
      return response;
    })
    .catch(async (error: any) => {
      trackInsightTrace(`${config.method} api error for ${config.url}`, {
        error: error,
        payload: config.method === 'POST' ? config.data : null,
        CorrelationId: config.headers.CorrelationId,
      });
      if (error?.response?.status === Constants.NOT_AUTHORIZED) {
        store.dispatch(authTokenActions.signOut());
      }
      if (numberOfAjaxCAllPending > 0) {
        numberOfAjaxCAllPending = numberOfAjaxCAllPending - 1;
      }
      if (numberOfAjaxCAllPending <= 0) {
        store.dispatch(appSliceActions.setAppLoading(false));
      }
      return error.response || error.message || apiPath;
    });
};

/*
Function to handle HTTP GET request
@params- for query params
*/
export const get = async (url: any, params: any, apiPath: any, fetchOnline: boolean = false,
  responseType: any = null,) => {
  const accessToken = await getAccessToken();

  const config: any = {
    baseURL: getBaseURL(url, params),
    method: 'GET',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    params,
  };
  // Add responseType if specified
  if (responseType) {
    config.responseType = responseType;
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/*
Function to handle HTTP POST request
@data for passing data as body
@params- for query params
*/
export const post = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
  responseType: any = null // <-- add this
) => {
  const accessToken = await getAccessToken();

  const config: any = {
    baseURL: getBaseURL(url, data), // Dynamic base URL based on API path and payload
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (responseType) {
    config.responseType = responseType; 
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const postAgenticExpense = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
  responseType: any = null // <-- add this
) => {
  const accessToken = await getAccessToken();
  const config: any = {
    baseURL: env.API_HOST_AGENTIC_EXPENSE,
    method: 'POST',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (responseType) {
    config.responseType = responseType; 
  }
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const getAgenticExpense = async (url: any, params: any, apiPath: any) => {
  const accessToken = await getAccessToken();

  const config = {
    baseURL: env.API_HOST_AGENTIC_EXPENSE,
    method: 'GET',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

export const postUploadFile = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();

  const config = {
    baseURL: getBaseURL(url, data), // Dynamic base URL based on API path and payload
    method: 'POST',
    url,
    headers: {
      'Content-Type':
        'multipart/form-data;boundary=----WebKitFormBoundaryVaou4bB7hlUIjuOY',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP POST request for multipart form data
 * @param {string} url http url of api
 * @param {FormData} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const postMultipartFormData = async (
  url: any,
  data: FormData,
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'POST',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request for multipart uploads
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const putUploadFile = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'PUT',
    url,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request for multipart form data
 * @param {string} url http url of api
 * @param {FormData} data data to pass in body
 * @param {object} params params to pass in api call
 * @param {object} apiPath api path for tracing
 */
export const putMultipartFormData = async (
  url: any,
  data: FormData,
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();
  const config = {
    baseURL: env.API_HOST,
    method: 'PUT',
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };
  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP PUT request
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 */
export const put = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();

  const config = {
    baseURL: getBaseURL(url, data), // Dynamic base URL based on API path and payload
    method: 'PUT',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/**
 * Handle HTTP DELETE request
 * @param {string} url http url of api
 * @param {object} data data to pass in body
 * @param {object} params params to pass in api call
 */
export const Delete = async (
  url: any,
  data: any = {},
  params: any = {},
  apiPath: any = null,
) => {
  const accessToken = await getAccessToken();

  const config = {
    baseURL: getBaseURL(url, data), // Dynamic base URL based on API path and payload
    method: 'DELETE',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      CorrelationId: generateUUID(),
    },
    data,
    params,
  };

  if (accessToken) {
    return getNetworkResponse(config, apiPath);
  }
};

/*
Function to handle HTTP GET request
@params- for query params
*/
export const getOpen = async (url: any, params: any, apiPath: any) => {
  const config = {
    baseURL: getBaseURL(url, params), // Dynamic base URL based on API path and params
    method: 'GET',
    url,
    params,
  };

  return getNetworkResponse(config, apiPath);
};

const NetworkService = {
  get,
  post,
  put,
  Delete,
  getOpen,
  postUploadFile,
  postAgenticExpense,
  getAgenticExpense,
  putUploadFile,
  putMultipartFormData,
  postMultipartFormData,
};

export default NetworkService;
